import React, { useEffect, useState, useCallback } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Card, Row, Col, Button, Dropdown } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getToken, getUserId } from "../services/authorize";
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from "../contexts/LoadingContext";
import { getCurrentSemester } from "../utils/teachingUtils";
import {
  TimetableGrid,
  buildTimetableGrid,
  hasTimetableData,
  exportTimetablePDF,
  printTimetable,
} from "./timetableGrid";

const API = process.env.REACT_APP_API;

export default function MyTimetable() {
  const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
  const [skin, setSkin] = useState(currentSkin);

  const switchSkin = (s) => {
    const tables = document.getElementsByTagName("table");
    for (const table of tables) {
      const theads = table.querySelectorAll("thead");
      if (s === "dark") {
        for (const thead of theads) { thead.classList.remove("table-secondary"); thead.classList.add("table-primary"); }
        table.classList.add("table-dark");
      } else {
        table.classList.remove("table-dark");
        for (const thead of theads) { thead.classList.remove("table-primary"); thead.classList.add("table-secondary"); }
      }
    }
  };
  switchSkin(skin);
  useEffect(() => { switchSkin(skin); }, [skin]);

  const { setLoading } = useLoading();

  const [yearList, setYearList] = useState([]);
  const [yearId, setYearId] = useState(() => localStorage.getItem("yearId") || null);
  const [yearLabel, setYearLabel] = useState("");
  const [periods, setPeriods] = useState([]);
  const [teachingData, setTeachingData] = useState([]);
  const [guidanceData, setGuidanceData] = useState([]); // คาบแนะแนวของห้องที่เป็นครูประจำชั้น
  const [semester, setSemester] = useState(getCurrentSemester()); // เริ่มที่ภาคเรียนปัจจุบัน
  const [teacherName, setTeacherName] = useState("");

  const authHeader = useCallback(() => ({
    headers: { authorization: `Bearer ${getToken()}` },
  }), []);

  // ─── Load year list ───────────────────────────────────────────────────────
  useEffect(() => {
    axiosInstance.get(`${API}/getStudyYear`, authHeader()).then((res) => {
      const list = res.data;
      setYearList(list || []);
      let id = localStorage.getItem("yearId");
      if (!id && list.length > 0) { id = list[0].year_id; localStorage.setItem("yearId", id); }
      setYearId(id);
      const sel = list.find((y) => String(y.year_id) === String(id));
      setYearLabel(sel ? `ปีการศึกษา ${sel.year_year}` : "");
    }).catch(() => {});
  }, []);

  const handleSelectYear = (id) => {
    const sel = yearList.find((y) => y.year_id === id);
    if (!sel) return;
    setYearId(id);
    setYearLabel(`ปีการศึกษา ${sel.year_year}`);
    localStorage.setItem("yearId", id);
  };

  // ─── Load periods ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!yearId) return;
    axiosInstance.get(`${API}/getPeriodList/${yearId}`, authHeader())
      .then((res) => setPeriods(res.data.map((p) => ({
        ...p,
        period_begin: p.period_begin?.slice(0, 5),
        period_end: p.period_end?.slice(0, 5),
      }))))
      .catch(() => setPeriods([]));
  }, [yearId]);

  // ─── Load teaching data ───────────────────────────────────────────────────
  useEffect(() => {
    if (!yearId) return;
    setLoading(true);
    axiosInstance.get(`${API}/getTeachingDataByTeacherId/${getUserId()}/${yearId}`, authHeader())
      .then((res) => {
        setTeachingData(res.data);
        if (res.data.length > 0 && res.data[0].t_name) {
          // ชื่อในฐานข้อมูลมีช่องว่างนำหน้า/ต่อท้ายปนอยู่ ซึ่งจะติดไปถึงชื่อไฟล์ PDF
          setTeacherName(String(res.data[0].t_name).trim());
        }
      })
      .catch((err) => {
        if (err.response?.status === 404) setTeachingData([]);
      })
      .finally(() => setLoading(false));
  }, [yearId]);

  // ─── Load guidance periods (คาบแนะแนว — เฉพาะครูที่เป็นครูประจำชั้น) ──────
  useEffect(() => {
    if (!yearId) return;
    axiosInstance.get(`${API}/getGuidanceByAdvisor/${getUserId()}/${yearId}`, authHeader())
      .then((res) => setGuidanceData(res.data || []))
      .catch(() => setGuidanceData([]));
  }, [yearId]);

  const grid = React.useMemo(
    () => buildTimetableGrid(teachingData, guidanceData, semester),
    [teachingData, guidanceData, semester]
  );

  const hasData = hasTimetableData(teachingData, guidanceData, semester);

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">

        {/* Top bar */}
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">ครูผู้สอน</Link></li>
              <li className="breadcrumb-item active">ตารางสอนของฉัน</li>
            </ol>
            <h4 className="main-title mb-0">ตารางสอนของฉัน</h4>
          </div>
          <div className="d-flex align-items-center gap-2 mt-3 mt-md-0">
            <Dropdown>
              <Dropdown.Toggle variant="primary" id="dropdown-year">
                {yearLabel || "เลือกปีการศึกษา"}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {yearList.map((y) => (
                  <Dropdown.Item key={y.year_id} onClick={() => handleSelectYear(y.year_id)}>
                    {`ปีการศึกษา ${y.year_year}`}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        <Row className="g-3">
          <Col xs={12}>
            <Card className="card-one">
              <Card.Header className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div>
                  {teacherName && (
                    <span className="fw-semibold">
                      <i className="ri-user-line me-1"></i>{teacherName}
                    </span>
                  )}
                </div>
                <div className="d-flex align-items-center gap-2">
                  <div className="btn-group" role="group">
                    <button
                      type="button"
                      className={`btn btn-sm ${semester === "1" ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => setSemester("1")}
                    >
                      เทอม 1
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${semester === "2" ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => setSemester("2")}
                    >
                      เทอม 2
                    </button>
                  </div>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    disabled={periods.length === 0 || !hasData}
                    onClick={() => exportTimetablePDF({ periods, grid, teacherName, yearLabel, semester })}
                  >
                    <i className="ri-file-pdf-line me-1"></i>Export PDF
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={periods.length === 0 || !hasData}
                    onClick={() => printTimetable({ periods, grid, teacherName, yearLabel, semester })}
                  >
                    <i className="ri-printer-line me-1"></i>พิมพ์
                  </Button>
                </div>
              </Card.Header>

              <Card.Body className="p-2">
                {periods.length === 0 ? (
                  <div className="text-center text-muted py-5">ยังไม่ได้กำหนดคาบเรียนสำหรับปีนี้</div>
                ) : !hasData ? (
                  <div className="text-center text-muted py-5">
                    <i className="ri-calendar-close-line fs-2 d-block mb-2"></i>
                    ไม่พบตารางสอนในเทอมนี้
                  </div>
                ) : (
                  <TimetableGrid periods={periods} grid={grid} semester={semester} />
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Footer />
      </div>
    </React.Fragment>
  );
}
